import { useQuery, useInfiniteQuery, keepPreviousData } from "@tanstack/react-query";

import {
  certificateActivityApi,
  CertificateActivityParams,
  CertificateActivityResponse,
} from "services/certificateActivityApi";

export const CERTIFICATE_ACTIVITY_QUERY_KEY = ["certificate", "activity"] as const;

export const useCertificateActivity = (
  certificateId: string,
  params?: CertificateActivityParams
) =>
  useQuery<CertificateActivityResponse>({
    queryKey: [...CERTIFICATE_ACTIVITY_QUERY_KEY, certificateId, params],
    queryFn: () => certificateActivityApi.getCertificateActivity(certificateId, params),
    staleTime: 30 * 1000,
    placeholderData: keepPreviousData,
    enabled: !!certificateId,
  });

export const ACTIVITY_PAGE_SIZE = 20;

// Infinite-scroll variant: fetch a page at a time by offset. getNextPageParam
// returns the next offset while fewer rows are loaded than `total`, else undefined
// (no more pages). The ActivityTab flattens pages.flatMap(p => p.data).
export const useCertificateActivityInfinite = (certificateId: string) =>
  useInfiniteQuery({
    queryKey: [...CERTIFICATE_ACTIVITY_QUERY_KEY, "infinite", certificateId],
    queryFn: ({ pageParam = 0 }) =>
      certificateActivityApi.getCertificateActivity(certificateId, {
        limit: ACTIVITY_PAGE_SIZE,
        offset: pageParam as number,
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((sum, p) => sum + p.data.length, 0);
      return loaded < lastPage.total ? loaded : undefined;
    },
    staleTime: 30 * 1000,
    enabled: !!certificateId,
  });
